import { debounce } from 'lodash';
import * as React from 'react';
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Grid,
    GridRow,
    Icon,
    Input,
    Pagination,
    PaginationProps,
    Select,
    Table,
} from 'semantic-ui-react';
import { InputOnChangeData } from 'semantic-ui-react/dist/commonjs/elements/Input/Input';
import { DropdownProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown';

import { ApiError } from '../../../application/axios';
import { AppDispatch, history, RootState } from '../../../application/store';
import {
    DEFAULT_PAGE_SELECTED,
    DEFAULT_PAGE_SIZE,
    PAGINATION_OPTIONS,
    PAGINATION_VALUES,
} from '../../common/pagination/PaginationConfig';
import {
    getIntegerValueInOrDefault,
    getPositiveIntegerValueOrDefault,
    getValueOrDefault,
    prepareSearch,
} from '../../common/search';
import HeaderCheckbox from '../../HeaderCheckbox';
import { fetchUsers } from '../reducer.Users';

import BatchActionDropdown from './BatchActionDropdown';
import UsersListRow from './UserListRow';

interface UserTableState {
    pageSizeSelected: number;
    pageSelected: number;
    filter: string;
}

function UserTable() {
    const { currentPage } = useSelector(
        (state: RootState) => state.persistedReducers.users.userList,
    );
    const search = useSelector((state: RootState) => state.router.location?.search) as string;
    const dispatch: AppDispatch = useDispatch<AppDispatch>();
    const location = useLocation();

    const filter = getValueOrDefault(search, 'filter', '');
    const page = getPositiveIntegerValueOrDefault(search, 'page', DEFAULT_PAGE_SELECTED);
    const perPage = getIntegerValueInOrDefault(
        search,
        'perPage',
        DEFAULT_PAGE_SIZE,
        PAGINATION_VALUES,
    );

    const [userState, setUserState] = useState<UserTableState>({
        pageSizeSelected: perPage,
        pageSelected: page,
        filter: filter,
    });

    const pushSearch = (filter: string, page: number, perPage: number) => {
        const search = prepareSearch(createSearchParam(filter, page, perPage));
        if (location.search !== '?' + search) {
            history.push({
                pathname: location.pathname,
                search: search,
            });
        }
    };

    const createSearchParam = (filter: string, page: number, perPage: number) => {
        return new Map<string, string>([
            ['filter', filter],
            ['page', page.toString()],
            ['perPage', perPage.toString()],
        ]);
    };

    const getUsers = async (page: number, perPage: number, filter: string) => {
        try {
            await dispatch(fetchUsers({ page: page, perPage: perPage, filter: filter })).unwrap();
        } catch (err) {
            if (err instanceof ApiError) toast.error(err.message);
        }
    };

    useEffect(() => {
        const { pageSelected, pageSizeSelected, filter } = userState;
        pushSearch(filter, pageSelected, pageSizeSelected);
        getUsers(pageSelected, pageSizeSelected, filter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState.pageSelected, userState.pageSizeSelected, userState.filter]);

    useEffect(() => {
        const filter = getValueOrDefault(location.search, 'filter', '');
        const page = getPositiveIntegerValueOrDefault(
            location.search,
            'page',
            DEFAULT_PAGE_SELECTED,
        );
        const perPage = getIntegerValueInOrDefault(
            location.search,
            'perPage',
            DEFAULT_PAGE_SIZE,
            PAGINATION_VALUES,
        );
        setUserState({ filter: filter, pageSelected: page, pageSizeSelected: perPage });
    }, [location.search]);

    const handleFilterChange = (
        event: ChangeEvent<HTMLInputElement>,
        { value }: InputOnChangeData,
    ) => {
        setUserState((prevState) => ({
            ...prevState,
            filter: value,
            pageSelected: 1,
        }));
    };

    const handlePaginationChange = (
        event: React.MouseEvent<HTMLAnchorElement>,
        { activePage }: PaginationProps,
    ) => {
        if (userState.pageSelected !== activePage) {
            setUserState((prevState) => ({
                ...prevState,
                pageSelected: activePage as number,
            }));
        }
    };

    const handlePageSizeChanged = (
        event: SyntheticEvent<HTMLElement, Event>,
        { value }: DropdownProps,
    ) => {
        if (userState.pageSizeSelected !== value) {
            setUserState((prevState) => ({
                ...prevState,
                pageSizeSelected: value as number,
                pageSelected: 1,
            }));
        }
    };

    const handleFilterChangeDebounce = debounce(handleFilterChange, 600);

    const getNumberOfPages = (recordCount: number) => {
        return Math.ceil(recordCount / userState.pageSizeSelected);
    };

    const numberOfPages = getNumberOfPages(currentPage.count);

    const userRows = currentPage.list.map((user) => (
        <UsersListRow key={user.legacyId} user={user} />
    ));
    const externalUsersCount = currentPage.list.filter((user) => !user.ldapId).length;

    return (
        <div>
            <div style={{ textAlign: 'left' }}>
                <Input
                    onChange={handleFilterChangeDebounce}
                    icon="search"
                    placeholder={'Search'}
                    defaultValue={userState.filter}
                />
                <span style={{ paddingLeft: '10px' }}>
                    <BatchActionDropdown
                        callback={() =>
                            getUsers(
                                userState.pageSelected,
                                userState.pageSizeSelected,
                                userState.filter,
                            )
                        }
                    />
                </span>
            </div>

            <Table celled striped size="small">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell collapsing>
                            <HeaderCheckbox rowsCount={externalUsersCount} />
                        </Table.HeaderCell>
                        <Table.HeaderCell>{'Legacy Id'}</Table.HeaderCell>
                        <Table.HeaderCell collapsing>{'Keycloak Id'}</Table.HeaderCell>
                        <Table.HeaderCell collapsing>{'LdapId'}</Table.HeaderCell>
                        <Table.HeaderCell>{'Username'}</Table.HeaderCell>
                        <Table.HeaderCell>{'First Name'}</Table.HeaderCell>
                        <Table.HeaderCell>{'Last Name'}</Table.HeaderCell>
                        <Table.HeaderCell>{'Email'}</Table.HeaderCell>
                        <Table.HeaderCell>{'User Type'}</Table.HeaderCell>
                        <Table.HeaderCell>{'User Status'}</Table.HeaderCell>
                        <Table.HeaderCell>{'Action'}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>{userRows}</Table.Body>
                <Table.Footer>
                    <Table.Row>
                        <Table.HeaderCell colSpan="11">
                            <Grid>
                                <GridRow centered style={{ paddingBottom: 0 }}>
                                    <Pagination
                                        activePage={userState.pageSelected}
                                        firstItem={null}
                                        lastItem={null}
                                        prevItem={{
                                            content: <Icon name="angle left" />,
                                            icon: true,
                                        }}
                                        nextItem={{
                                            content: <Icon name="angle right" />,
                                            icon: true,
                                        }}
                                        totalPages={numberOfPages}
                                        onPageChange={handlePaginationChange}
                                    />
                                </GridRow>
                                <GridRow centered>
                                    <Select
                                        placeholder={'Page size'}
                                        options={PAGINATION_OPTIONS}
                                        onChange={handlePageSizeChanged}
                                        defaultValue={userState.pageSizeSelected}
                                    />
                                </GridRow>
                            </Grid>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </div>
    );
}

export default UserTable;
